import {
  PASSWORD_MASK,
  ProbePrefill,
  SiteConnectionDetail,
} from "../troubleshootingInterfaces";
import { primaryPorts } from "./integrationEndpoints";

/** The ports a site's settings name for its server: API/HTTP, then stream. */
export const sitePorts = (detail: SiteConnectionDetail | null): number[] => {
  // The ports the connector dials for this integration (defaults included).
  const fromProfile = primaryPorts(detail);
  if (fromProfile.length) return fromProfile;
  return Array.from(
    new Set(
      [detail?.server_port, detail?.stream_port].filter(
        (port): port is number => typeof port === "number" && port > 0
      )
    )
  );
};

/**
 * A secret the caller may actually use. admin sends PASSWORD_MASK in place of
 * a password the user may not see; that must never reach a form or a probe.
 */
export const usableSecret = (value: string | null | undefined): string | null =>
  value && value !== PASSWORD_MASK ? value : null;

/** A camera's saved login, from `camera/{id}/general_info/`. */
export interface CameraLogin {
  username?: string | null;
  password?: string | null;
}

export interface CredentialPrefill {
  host: string;
  port: string | null;
  rtspPort: string | null;
  username: string | null;
  password: string | null;
  /** Where the login came from, for the hint under the form. */
  loginSource: "site" | "camera" | null;
  /**
   * The site has a saved login but this user may not see it
   * (no view_passwords), so it was deliberately left blank.
   */
  withheld: boolean;
}

/**
 * What "Check reachability" fills into the Credential test. Host and ports
 * are already visible in the finding, so everyone gets them. The login is
 * filled only for users with view_passwords — the same rule admin applies
 * to the password itself — and the username goes with it, so a user without
 * the permission never sees a partial login.
 */
export const buildCredentialPrefill = ({
  prefill,
  detail,
  canViewPasswords,
  camera,
}: {
  prefill: ProbePrefill;
  detail: SiteConnectionDetail | null;
  canViewPasswords: boolean;
  camera?: CameraLogin | null;
}): CredentialPrefill => {
  const sameSite =
    detail != null && prefill.siteId != null && detail.id === prefill.siteId;
  const siteDetail = sameSite ? detail : null;
  const port =
    siteDetail?.server_port && siteDetail.server_port > 0
      ? String(siteDetail.server_port)
      : null;
  const rtspPort =
    siteDetail?.stream_port && siteDetail.stream_port > 0
      ? String(siteDetail.stream_port)
      : null;

  const siteHasLogin = Boolean(siteDetail?.username || siteDetail?.password);
  const cameraHasLogin = Boolean(camera?.username || camera?.password);
  const base = { host: prefill.host, port, rtspPort };

  if (!canViewPasswords) {
    return {
      ...base,
      username: null,
      password: null,
      loginSource: null,
      withheld: siteHasLogin || cameraHasLogin,
    };
  }
  if (siteHasLogin) {
    return {
      ...base,
      username: siteDetail?.username || null,
      password: usableSecret(siteDetail?.password),
      loginSource: "site",
      withheld: false,
    };
  }
  if (cameraHasLogin) {
    return {
      ...base,
      username: camera?.username || null,
      password: usableSecret(camera?.password),
      loginSource: "camera",
      withheld: false,
    };
  }
  return {
    ...base,
    username: null,
    password: null,
    loginSource: null,
    withheld: false,
  };
};
